/* eslint-disable @typescript-eslint/indent */
import React, { useEffect, useRef, useState, useMemo } from 'react';
import Image from 'next/legacy/image';
import ReactMarkdown from 'react-markdown';
import isShowReadMoreText from '@helpers/readMore';
import warningIcon from '@assets/images/warning-icon-2.svg';
import { getReadyTabClicked, productsTabClicked, reviewsTabClicked } from '@events/DLP';
import AmenitiesBottomDrawer from '@components/common/BottomDrawer/AmenitiesBottomDrawer';
import { Tab, TabComponent } from '@components/common/Tab/TabComponent';
import locationPin from '@assets/images/location-pin.svg';
import HotSpotBanner from '@components/HotSpot/HotSpotBanner';
import REVIEWS_THRESHOLD_TO_BE_SHOWN from '@constants/yotpo';
import StarRating from '@components/common/Yotpo/StarRating';
import Reviews from '@components/common/Yotpo/Reviews';
import { Separator } from '@solar/common/Separator';
import { useEvents } from '@events/EventsProvider';
import { VIEW_DLP_REVIEWS } from '@constants/amplitudeEvents';
import useAmplitudePayloads from '@hooks/useAmplitudePayloads';
import Badge from '@components/common/Badge/Badge';
import { useHotelContext } from '@context/HotelContext';
import { useFeatureFlagContext } from '@context/FeatureFlagContext';
import { isTreatment } from '@helpers/experiment';
import TruncateText from '@components/common/TruncateText/TruncateText';
import styles from './MobileHotelDetails.module.css';
import MarkdownHotelDescription from './MarkdownHotelDescription';
import ProductDetailsTab from './ProductDetailsTab';
import GetReadyTab from './GetReadyTab';

type Props = {
  setHideReviews: React.Dispatch<React.SetStateAction<boolean>>;
  hideReviews: boolean;
  setHideReviewsHeader: React.Dispatch<React.SetStateAction<boolean>>;
  hideReviewsHeader: boolean;
  averageScore: number;
  totalReviews: number;
};

export default function MobileHotelDetails({
  setHideReviews,
  hideReviews,
  setHideReviewsHeader,
  hideReviewsHeader,
  averageScore,
  totalReviews,
}: Props) {
  const { date, hotel: hotelDetails, productDetails } = useHotelContext();

  const psaWarningRef = useRef<any>(null);
  const separatorRef = useRef<any>(null);

  const [showFullPSAWarning, setShowFullPSAWarning] = useState<boolean>(true);
  const [psaWarningFullHeight, setPsaWarningFullHeight] = useState<number>(0);
  const [showPSAWarningReadMoreText, setShowPSAWarningReadMoreText] = useState<boolean>(true);
  const [isAmenitiesDrawerOpen, setIsAmenitiesDrawerOpen] = useState<boolean>(false);
  const [activeTab, setActiveTab] = useState<number>(0);
  const [reviewsCount, setReviewsCount] = useState(0);

  const { summarizedDescriptionExperiment } = useFeatureFlagContext();
  const isShowingChatGPTDescription = useMemo(
    () =>
      Boolean(hotelDetails.summarized_description) && isTreatment(summarizedDescriptionExperiment),
    [hotelDetails.summarized_description, summarizedDescriptionExperiment],
  );

  const togglePSAWarning = () => {
    setShowFullPSAWarning(!showFullPSAWarning);
  };

  const scrollTabIntoView = () => {
    if (separatorRef.current && separatorRef.current.scrollIntoView) {
      separatorRef.current.scrollIntoView({
        behavior: 'instant' as ScrollBehavior,
        block: 'start',
      });
    }
  };

  const { track } = useEvents();
  const { generateHotelPayload } = useAmplitudePayloads();
  const hotelPropertyGroup = generateHotelPayload(hotelDetails, undefined);

  const onStarWidgetClick = () => {
    // Set Reviews tab as active
    setActiveTab(1);

    track(VIEW_DLP_REVIEWS, {
      view_source: 'Star Widget',
      number_of_reviews: reviewsCount,
      not_enough_reviews: false,
      ...hotelPropertyGroup,
      hotel_reviews_count: reviewsCount,
    });
  };

  useEffect(() => {
    scrollTabIntoView();
  }, [activeTab]);

  useEffect(() => {
    if (psaWarningRef && psaWarningRef.current) {
      const divHeight = psaWarningRef.current.offsetHeight;
      setPsaWarningFullHeight(divHeight);
      setShowPSAWarningReadMoreText(isShowReadMoreText(psaWarningRef, 4));
      setShowFullPSAWarning(false);
    }
  }, []);

  // remove read more button when clipped height of psa banner is same as it's full height
  useEffect(() => {
    if (!showFullPSAWarning && psaWarningFullHeight && psaWarningRef && psaWarningRef.current) {
      const divHeight = psaWarningRef.current.offsetHeight;
      if (divHeight === psaWarningFullHeight) {
        setShowPSAWarningReadMoreText(false);
      }
    }
  }, [showFullPSAWarning, psaWarningFullHeight]);

  const productId =
    productDetails?.products[0]?.product_id ??
    productDetails?.products[0]?.children?.[0]?.product_id;

  const HotelDescriptionComponent = isShowingChatGPTDescription ? (
    <MarkdownHotelDescription>{hotelDetails.summarized_description}</MarkdownHotelDescription>
  ) : (
    <TruncateText lines={5}>{hotelDetails.description}</TruncateText>
  );

  return (
    <div>
      <div className="flex flex-col w-full text-base dlp-left-container font-rp-pn-regular -tracking-0.24 text-rp-primary-black d:w-2/3 d:pl-0 d:pr-0 d:text-17">
        <div
          data-testid="hotel-details-header"
          className="flex flex-col justify-between items-start d:flex-row d:mb-2 px-4"
        >
          {/* Hotel Name and Star Rating */}
          <div className="flex flex-col">
            <div className="flex items-center space-x-1">
              <div className="h-4 relative w-4">
                <Image
                  src={locationPin}
                  alt={`${hotelDetails.city_name}, ${hotelDetails.code}`}
                  layout="fill"
                />
              </div>

              <p className="font-semibold leading-6 text-sm">
                {hotelDetails.city_name}, {hotelDetails.code}
              </p>
            </div>

            <h2 className="text-25 leading-8 w-full font-rp-pn-semi-bold break-normal d:w-auto d:max-w-sm">
              {hotelDetails.name}
            </h2>
            <div className="mt-2 mb-2">
              <StarRating
                productId={productId}
                reviewsThreshold={REVIEWS_THRESHOLD_TO_BE_SHOWN}
                onClick={onStarWidgetClick}
                setHideReviews={setHideReviews}
                setReviewsCount={setReviewsCount}
                setHideReviewsHeader={setHideReviewsHeader}
                averageScore={averageScore}
                totalReviews={totalReviews}
              />
            </div>
            <div className="flex flex-wrap items-center space-x-2">
              {hotelDetails.star_classification && (
                <Badge
                  tagClass="bg-rp-primary-new text-white"
                  tagContent={`${hotelDetails.star_classification} Star Property`}
                  upperCase={false}
                />
              )}

              {hotelDetails.vibes && hotelDetails.vibes.primary && (
                <Badge
                  tagClass="bg-rp-primary-new text-white"
                  tagContent={hotelDetails.vibes.primary}
                  upperCase={false}
                />
              )}
            </div>
          </div>
        </div>

        {/* PSA Warning */}
        {hotelDetails.metadata && hotelDetails.metadata?.public_service_message?.trim() && (
          <div className="rounded-lg bg-rp-toast-warning shadow-toast py-6 px-4 relative mt-8 mx-4 -mb-2">
            <div className={`flex ${styles.listContainer} ${styles.psaBanner}`} id="psa-container">
              <div className="relative h-22.5px w-22.5px mr-8.75px flex-shrink-0">
                <Image src={warningIcon} layout="fill" alt="Warning Icon" />
              </div>

              <div className="flex flex-col items-start font-rp-pn-regular relative overflow-hidden">
                <div
                  ref={psaWarningRef}
                  className={`${
                    showFullPSAWarning
                      ? 'line-clamp-none overflow-hidden'
                      : 'line-clamp-4 overflow-hidden max-h-28 d:max-h-32'
                  }`}
                >
                  <ReactMarkdown>{hotelDetails.metadata.public_service_message}</ReactMarkdown>
                </div>

                {showPSAWarningReadMoreText && (
                  <button
                    className="underline font-rp-pn-semi-bold text-rp-primary-dark mt-1"
                    type="button"
                    onClick={togglePSAWarning}
                  >
                    {showFullPSAWarning ? 'read less' : 'read more'}
                  </button>
                )}
              </div>
            </div>
          </div>
        )}

        {/* HOT SPOT */}
        {date ? <HotSpotBanner hotel={hotelDetails} /> : null}

        {/* Hotel Description */}
        <div
          data-testid="hotel-description"
          className="flex flex-col px-4 my-spacing-lg gap-y-spacing-lg"
        >
          <Separator className="h-px" />
          {HotelDescriptionComponent}
          <Separator className="h-px" />
        </div>

        <div ref={separatorRef} />

        <TabComponent activeTab={activeTab} setActiveTab={setActiveTab}>
          <Tab
            name="Products"
            onClickEvent={() => {
              productsTabClicked(hotelDetails);
            }}
          >
            <ProductDetailsTab setIsAmenitiesDrawerOpen={setIsAmenitiesDrawerOpen} />
          </Tab>

          <Tab
            name="Reviews"
            onClickEvent={() => {
              reviewsTabClicked(hotelDetails);
              track(VIEW_DLP_REVIEWS, {
                view_source: 'Reviews Tab',
                number_of_reviews: reviewsCount,
                not_enough_reviews: hideReviews,
                ...hotelPropertyGroup,
                hotel_reviews_count: reviewsCount,
              });
            }}
          >
            <div className="mt-5 mx-4">
              <Reviews
                productId={productId}
                hideReviews={hideReviews}
                hideReviewsHeader={hideReviewsHeader}
              />
            </div>
          </Tab>

          <Tab
            name="Get Ready"
            onClickEvent={() => {
              getReadyTabClicked(hotelDetails);
            }}
          >
            <div className="mt-5">
              <GetReadyTab hotel={hotelDetails} />
            </div>
          </Tab>
        </TabComponent>
      </div>

      {/* Amenities Drawer */}
      {isAmenitiesDrawerOpen && hotelDetails.amenities && (
        <AmenitiesBottomDrawer
          amenities={hotelDetails.amenities}
          isAmenitiesDrawerOpen={isAmenitiesDrawerOpen}
          setIsAmenitiesDrawerOpen={setIsAmenitiesDrawerOpen}
        />
      )}
    </div>
  );
}
